import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { useThunk } from "../../hooks/useThunk";
import { getExperienceInvitation } from "../../store/thunks/experience/getExperienceInvitation";
import { getExperienceRegistration } from "../../store/thunks/experience/getExperienceRegistration";
import { extractDateTime } from "../utils/extractDateTime";
import { extracDeadLineTime } from "../utils/extractDeadLineTime";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "../utils/capitalizerLetter";
import { getExperiencePayment } from "../../store/thunks/experience/getExperiencePayment";
import PersonalInfoGroup from "../profile/web/PersonalInfoGroup";

const actionConfirmPayment = async (sessionId, actionType, experiment_id, amount, isOnline = false) => {
  try {
    const formData = new FormData();
    formData.append("session_id", sessionId);
    formData.append("experiment_id", experiment_id);
    formData.append("amount", amount);
    formData.append("is_online", isOnline ? "1" : "0");

    const response = await axiosInstance.post(
      `/participant/${actionType}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error registering participant:", error);
    return { error: error.response ? error.response.data : "Unknown error" };
  }
};


const steps = ['personal_info_verification', 'payment_confirmation'];

const PaymentDialog = ({ open, onClose, experience, onConfirm, type, data }) => {
  const [loading, setLoading] = useState(false);
  const [runPaymentConfirmation, isLoadingConfirmation, errorConfirmation] = useThunk(
    getExperiencePayment
  );
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [autoSave, setAutoSave] = useState(false);
  
  const [activeStep, setActiveStep] = React.useState(0);
  const [attest, setAttest] = React.useState(0);
  const [accept, setAccept] = React.useState(0);

  const handleNext = () => {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveProfile = (result) => {
    setAutoSave(false);
    console.log(result);
    if (result) {
      handleNext();
    }
  };

  const handleConfirmClick = async () => {
    if (experience) {
      setLoading(true);
      const result = await actionConfirmPayment(
        experience.session_id,
        "confirm_payment",
        experience.experiment_id,
        experience.payment_amt,
        experience.experiment_type === "internet"
      );

      if (result) {
        await runPaymentConfirmation();
      }
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: isMobile ? "1.2rem" : "1.5rem", 
          color: "text.accent"
        }}
      >
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
        {experience && activeStep === steps.length -1 ? (
            <Box sx={{ margin: "2em .5em"}}>
              <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontSize: isMobile ? "1rem" : "1.2rem",
                  }}
                >
                  {t("participant_payment_confirmation_data_information")}
              </Typography>
                <Box sx={{ padding: "1em", border: `1px solid ${theme.palette.text.accent}`}}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      mb: 2,
                      fontSize: isMobile ? "1rem" : "1.2rem",
                    }}
                  >
                    {capitalizeFirstLetter(
                      t("experiment").replace(/<\/?[^>]+(>|$)/g, "")
                    )}{" "}
                    {experience.experiment_public_name}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "bold",
                        fontSize: isMobile ? "0.9rem" : "1rem",
                      }}
                    >
                    {t("date_and_time").replace(/<\/?[^>]+(>|$)/g, "")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: isMobile ? "0.9rem" : "1rem",
                        mt: isMobile ? 0.5 : 0, 
                      }}
                    >
                      {`${extractDateTime(experience.session_name).date} - ${
                        extractDateTime(experience.session_name).time
                      }`}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: isMobile ? "0.9rem" : "1rem",
                        mt: isMobile ? 0.5 : 0, 
                      }}
                    >
                      {`${experience.payment_amt} CHF`}
                    </Typography>
                  </Box>
              
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                      flexDirection: isMobile ? "column" : "row",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "bold",
                        fontSize: isMobile ? "0.9rem" : "1rem",
                      }}
                    >
                      {t("location").replace(/<\/?[^>]+(>|$)/g, "")}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: isMobile ? "0.9rem" : "1rem",
                        mt: isMobile ? 0.5 : 0,
                      }}
                    >
                      {experience.lab_name}
                    </Typography>
                  </Box>
              </Box>
              
              <FormControl component="fieldset" sx={{ marginBottom: "20px", marginTop: "20px" }}>
                <FormLabel sx={{color: theme.palette.text.primary}} component="legend">
                  {t("avs_payment_condition")}
                </FormLabel>
                <RadioGroup
                  row
                  aria-label="attest_less_then_amount"
                  name="attest_less_then_amount"
                  value={attest}
                  onChange={(e) => setAttest(e.target.value)}
                >
                  <FormControlLabel
                    value="y"
                    control={<Radio sx={{color: theme.palette.text.primary}}/>}
                    label={t("yes")}
                  />
                  <FormControlLabel
                    value="n"
                    control={<Radio sx={{color: theme.palette.text.primary }}/>}
                    label={t("no")}
                  />
                </RadioGroup>
              </FormControl>
              <FormControl component="fieldset" sx={{ marginBottom: "20px", marginTop: "20px" }} onChange={(e) => setAccept(e.target.checked ? "y" : "n")}>
                <FormLabel sx={{color: theme.palette.text.primary}} component="legend">
                  {t("payment_confirmation_validation")}
                </FormLabel>
                <FormControlLabel
                    value="y"
                    control={<Checkbox sx={{color: theme.palette.text.primary}} />}
                    label={t("payment_confirmation_valid_accept")}
                  />               
                </FormControl>
            </Box>
        ): (<>
          <Typography variant="body1" gutterBottom sx={{ marginBottom: "20px",color: theme.palette.text.accent }}>
                {t("bank_transfer_explanation")
                  .replace(/<[^>]*>/g, "")
                  .trim()}
              </Typography>
          <PersonalInfoGroup data={data} isEditable={true} autoSave={autoSave} autoSaveCB={handleSaveProfile} />
        </>)}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "space-between",
          p: isMobile ? 2 : 3,
          flexDirection: isMobile ? "column" : "row", 
          mb:isMobile ? 10 : 0
        }}
      >
        <Button
          onClick={onClose}
          color="error"
          variant="outlined"
          sx={{
            fontSize: isMobile ? "0.8rem" : "1rem", 
            width: isMobile ? "100%" : "auto", 
            mb: isMobile ? 3 : 0, 
          }}
          disabled={loading}
        >
          {t("no_sorry")}
        </Button>
        <Button
              color="inherit"
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
          <u>Back</u>
        </Button>
        {activeStep === steps.length -1 &&<Button
          onClick={handleConfirmClick}
          color="primary"
          variant="contained"
          sx={{
            fontSize: isMobile ? "0.8rem" : "1rem", 
            width: isMobile ? "100%" : "auto",
          }}
          disabled={loading || accept !== "y"}
        >
          {loading ? <CircularProgress size={24} /> : t("tofinalize")}
        </Button>}
        {activeStep < steps.length -1 && <Button
            onClick={() => setAutoSave(Math.random())}
            color="primary"
            variant="contained"
            sx={{
              fontSize: isMobile ? "0.8rem" : "1rem", 
              width: isMobile ? "100%" : "auto", 
              mb: isMobile ? 3 : 0, 
            }}
          >
            {'Next'}
          </Button>
        }
      </DialogActions>

      {loading && (
        <Backdrop
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1201,
            color: "#fff",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Dialog>
  );
};

export default PaymentDialog;
